import React, { useState } from "react";
import { FlowPrimaryButton } from "@/components/consultation-flow/ui";
import type { ClinicianAssessmentNode as ClinicianAssessmentNodeType } from "@/lib/pharmacyFirst/types";

export interface ClinicianAssessmentAnswer {
  notes: string;
  conclusion: "optionA" | "optionB";
  conclusionLabel: string;
}

interface ClinicianAssessmentNodeProps {
  node: ClinicianAssessmentNodeType;
  isDarkMode?: boolean;
  stepNumber?: number;
  /** Previously given answer, re-seeded when the pharmacist steps back. */
  value?: ClinicianAssessmentAnswer | null;
  onAnswer: (answer: ClinicianAssessmentAnswer) => void;
}

export function ClinicianAssessmentNode({
  node,
  isDarkMode = false,
  stepNumber,
  value,
  onAnswer,
}: ClinicianAssessmentNodeProps) {
  const [notes, setNotes] = useState(value?.notes ?? "");
  const [conclusion, setConclusion] = useState<"optionA" | "optionB" | null>(
    value?.conclusion ?? null
  );

  const canContinue = notes.trim().length > 0 && conclusion !== null;

  const handleContinue = () => {
    if (!canContinue || conclusion === null) return;
    onAnswer({
      notes: notes.trim(),
      conclusion,
      conclusionLabel:
        conclusion === "optionA"
          ? node.conclusions.optionA.label
          : node.conclusions.optionB.label,
    });
  };

  const cardClass = `border rounded-2xl p-4 ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"}`;

  return (
    <div className={cardClass}>
      <div className="flex items-center gap-3 mb-4">
        {stepNumber !== undefined && (
          <div
            className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold flex-shrink-0 ${isDarkMode ? "bg-white text-black" : "bg-black text-white"}`}
          >
            {stepNumber}
          </div>
        )}
        <div className="flex-1">
          <h3 className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
            {node.text}
          </h3>
          {node.hint && (
            <p className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
              {node.hint}
            </p>
          )}
        </div>
      </div>

      <div className="mb-4">
        <label className={`text-xs font-medium block mb-1.5 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
          {node.notesPrompt}
          <span className={`ml-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
            (required)
          </span>
        </label>
        <textarea
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Document your assessment and reasoning…"
          aria-label={node.notesPrompt}
          className={`w-full text-sm rounded-lg border px-3 py-2 resize-none focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${isDarkMode
            ? "border-gray-600 bg-gray-700 text-gray-100 placeholder-gray-500"
            : "border-gray-300 bg-white text-gray-900 placeholder-gray-400"
          }`}
        />
      </div>

      <div className="mb-4">
        <p className={`text-xs font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
          Conclusion
        </p>
        <div className="space-y-2">
          {(["optionA", "optionB"] as const).map((opt) => {
            const label =
              opt === "optionA"
                ? node.conclusions.optionA.label
                : node.conclusions.optionB.label;
            const selected = conclusion === opt;

            return (
              <button
                key={opt}
                type="button"
                aria-pressed={selected}
                onClick={() => setConclusion(opt)}
                className={`w-full text-left px-4 py-3 rounded-lg border-2 text-sm font-medium transition select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${selected
                  ? isDarkMode
                    ? "border-blue-500 bg-blue-950/40 text-blue-100"
                    : "border-blue-500 bg-blue-50 text-blue-900"
                  : isDarkMode
                    ? "border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600"
                    : "border-gray-300 bg-white text-gray-800 hover:bg-gray-50"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <FlowPrimaryButton
        label="Continue"
        className="w-full"
        onClick={handleContinue}
        disabled={!canContinue}
        blockedReason={
          notes.trim().length === 0
            ? "Enter your clinical notes to continue."
            : "Select an outcome to continue."
        }
      />
    </div>
  );
}
